import { useMemo, useSyncExternalStore } from 'react';
import { Bell } from '../../icons';
import { type CalendarDto, type CalendarEventDto } from '../../api/calendar';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { formatTime } from '../../lib/base-i18n';
import { eventsOnDay, isSameCalDay } from '../../lib/calendar-shell';
import { Avatar } from '../../components/shared/Avatar';
import { TypeIcon, typeMeta } from './calendarTypeMeta';
import { isEventAssignedToMe } from './eventAssignment';
import { calendarLabelOf } from './lib/calendarLabel';
import { ReservationBadge } from './ReservationBadge';
import { agendaRangeLabel } from './lib/agendaRangeLabel';

/**
 * 일간 보기 — 시간표(24시간 격자) 대신 **시간 축 + 표** (2026-09-09 사용자 결정).
 *
 * 왼쪽 열은 시작–종료 시각(시간 축), 오른쪽은 캘린더 · 제목 · 유형 · 장소 · 참석자 열의 표.
 * 빈 시간대는 그리지 않는다(내가 안 들어간 일정도 섞여 있어 "빈 시간" 이 의미가 없다 — 사용자).
 * 하루종일 일정은 맨 위에 「종일」 행으로. 참석자가 아닌 일정은 다른 뷰처럼 흐리게.
 *
 * 주간은 기존 TimeGridView 그대로 — CalendarPage 가 activeView==='day' 일 때만 이 컴포넌트를 쓴다.
 *
 * ⛔ **일간에서 「끌어서 시각 옮기기」가 사라진다.** 격자 위 픽셀이 곧 시각이라 가능했던 것이고,
 *   표는 줄이라 끌 자리가 없다. 정본(feature/dev-kyeongeun)도 `onEventMove` 를 **안 넘긴다** —
 *   그것이 2026-09-09 사용자 결정이다. **주간에는 그대로 남는다.**
 *
 * ⏸ **아직 비어 있는 칸 넷** — 각각 무엇이 오면 차는지 아래 자리마다 적어 두었다.
 *   참석자 열 · 알림 칩 · 회의록 단추 · 회의실 배지.
 */
interface DayAgendaViewProps {
  date: Date;
  events: CalendarEventDto[];
  calendars: CalendarDto[];
  loading: boolean;
  myUserId: string | null;
  onEventClick: (e: CalendarEventDto) => void;
}

const MAX_AVATARS = 4; // 아바타 크기(2026-09-09): xxs(14) → sm(32) 는 너무 커서 그 중간 xs(24px)

/**
 * 12시간제 시(時) — ⭐ **자정은 `0`**, 정오는 `12`.
 *
 * ⚠ **2026-10-06 — 「0시·12시는 12」였던 것을 고쳤다**(사용자 결정 「네」).
 * ```
 * 종전: getHours() % 12 || 12   ⇒ 자정 ★ 12 · 정오 ★ 12   ⇒ ⛔ 「오전 12」가 나왔다
 * 지금: 자정만 0                 ⇒ 자정 ⭐ 0  · 정오 ★ 12   ⇒ ✅ 「오전 0」
 * ```
 * ⇒ ★ 같은 결정을 `base-i18n.formatTime` 에도 걸었다 — ⭐ 그 머리말에 **왜 ko 만인지**와
 *   **왜 정오는 그대로인지**가 적혀 있다. ⛔ 여기만 고치면 ★ 두 자리가 다른 말을 한다.
 * ⚠ 이 화면은 **12시간제를 손으로** 그린다(오전/오후는 셀 캡션이 맡는다) ⇒ 그래서 `formatTime`
 *   을 그대로 못 쓰고 **같은 규칙을 여기에도** 둔다. 그 둘이 어긋나면 시험이 빨개진다.
 */
function hour12(d: Date): number {
  const h = d.getHours();
  if (h === 0) return 0;          // ⭐ 자정 — 「오전 0시」
  return h % 12 || 12;            // ★ 정오(12)는 그대로 12
}
/** 알림 칩 문구 — EventDetailModal.reminderLabel 과 같은 규칙. */
function reminderLabel(min: number): string {
  if (min === 0) return tCalendar('calendar.reminder.at_time');
  if (min % 1440 === 0) return tCalendar('calendar.reminder.day_before', { n: min / 1440 });
  if (min % 60 === 0) return tCalendar('calendar.reminder.hour_before', { n: min / 60 });
  return tCalendar('calendar.reminder.min_before', { n: min });
}
/** 12시간제 "h:mm" (오전/오후 표기는 셀의 캡션이 맡는다). */
function hm12(d: Date): string { return `${hour12(d)}:${String(d.getMinutes()).padStart(2, '0')}`; }

export function DayAgendaView({ date, events, calendars, loading, myUserId, onEventClick }: DayAgendaViewProps) {
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  // 일간 표에는 툴팁을 안 띄운다(2026-09-09 사용자 지시) — 제목·설명·참석자가 이미 표에 다 보인다.

  const { allDay, timed } = useMemo(() => {
    const onDay = eventsOnDay(events, date);
    const allDayList = onDay.filter((e) => e.isAllDay);
    const timedList = onDay.filter((e) => !e.isAllDay).sort((a, b) => a.startAt.localeCompare(b.startAt) || a.endAt.localeCompare(b.endAt));
    return { allDay: allDayList, timed: timedList };
  }, [events, date]);

  const isToday = isSameCalDay(date, new Date());
  const now = new Date();

  function calendarOf(e: CalendarEventDto) {
    const cal = e.calendarId ? calendars.find((c) => c.id === e.calendarId) : undefined;
    return { label: calendarLabelOf(e, calendars), color: cal?.color ?? 'var(--text-muted)' };
  }

  /**
   * 참석자 줄 — 이름이 있어야 아바타를 그린다.
   * ⏸ **지금은 늘 비어 있다.** 서버가 `attendees[].name` 도 `teamMembers` 도 안 보내기 때문이다
   *   (api/calendar.ts 의 그 두 칸 주석에 만료 조건을 적어 두었다).
   *   ⇒ 그래서 이 열은 「—」로 그려진다. **열과 폭은 정본 그대로 둔다** — 열을 빼면 colgroup 이
   *     달라져 「디자인 그대로」가 깨지고, 비어 있는 칸이 「다음에 무엇이 와야 하나」를 보여 준다.
   *   ⇒ 그 칸들이 오면 이 함수는 **고칠 것이 없다** — 저절로 찬다.
   */
  function peopleOf(e: CalendarEventDto): Array<{ key: string; label: string }> {
    if (e.attendees.length > 0) {
      return e.attendees.filter((a) => a.name).map((a) => ({ key: a.userId, label: a.name! }));
    }
    return (e.teamMembers ?? []).map((m) => ({ key: m.userId, label: m.name }));
  }

  /**
   * 시간 셀 아래 알림(시안 B, 2026-09-09) — 내가 설정한 것만.
   * ⏸ **지금은 늘 null 이다** — 목록 응답에 `reminders` 가 없다(상세 라우트에만 있다).
   *   · 만료 조건 — 목록 응답에 그 칸이 실리면.  · 그때 할 일 — 이 함수는 고칠 것이 없다.
   *     이어서 a3(「상세에서 알림을 바꾸면 표가 새로고침 없이 따라온다」)를 얹는다.
   * ⭐ 2026-10-13 — 두 조건 다 «발동했다»: 목록 응답에 `reminders` 가 실리고(#2485),
   *   상세의 변경이 `onRemindersChange` 로 목록을 고친다(CalendarPage · reminder-refresh 시험). ⇒ 위 ⏸ 는 지난 말이다.
   */
  function renderReminders(e: CalendarEventDto) {
    const list = e.reminders ?? [];
    if (list.length === 0) return null;
    return (
      <span className="cal-agenda-reminders">
        {list.map((r) => (
          <span key={r.id} className="cal-agenda-reminder"><Bell size={10} aria-hidden />{reminderLabel(r.minutesBefore)}</span>
        ))}
      </span>
    );
  }

  function renderRow(e: CalendarEventDto, timeCell: React.ReactElement) {
    const meta = typeMeta(e.type);
    const cal = calendarOf(e);
    const people = peopleOf(e);
    const assigned = isEventAssignedToMe(e, myUserId);
    const key = `${e.id}:${e.occurrenceAt ?? ''}`;
    return (
      <tr
        key={key}
        className={`cal-agenda-row${assigned ? ' cal-agenda-row--mine' : ''}${e.type === 'todo' && e.done ? ' cal-agenda-row--done' : ''}`}
        style={{ '--seg-c': meta.color } as React.CSSProperties}
        onClick={() => onEventClick(e)}
        tabIndex={0}
        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onEventClick(e); } }}
      >
        {timeCell}
        <td className="cal-agenda-cal">
          <span className="cal-agenda-cal-dot" style={{ background: cal.color }} aria-hidden />
          <span className="cal-agenda-cal-name">{cal.label}</span>
        </td>
        <td className="cal-agenda-title">
          {/* 회의실 승인 배지 — 2026-10-01 에 섰다(서버가 `reservations` 를 주기 시작한 판). */}
          <span className="cal-agenda-title-text"><ReservationBadge event={e} variant="small" />{e.title}</span>
          {/* ⏸ 회의록 바로가기가 제목 뒤에 온다 — Ⓐ⑥ 이 서면 `<MinutesGoButton event={e} … />`. */}
          {/* 설명은 제목 아래 둘째 줄(시안 B, 2026-09-09) — 두 줄까지, 넘치면 말줄임 */}
          {e.description && <span className="cal-agenda-desc">{e.description}</span>}
        </td>
        <td className="cal-agenda-type">
          <span className="calendar-legend-item cal-agenda-type-chip">
            <span className="calendar-legend-ico" style={{ color: meta.color }} aria-hidden><TypeIcon type={e.type} size={13} /></span>
            {meta.label}
          </span>
        </td>
        <td className="cal-agenda-loc">{e.location || <span className="cal-agenda-none">—</span>}</td>
        <td className="cal-agenda-people">
          {people.length > 0 ? (
            <span className="cal-agenda-avatars">
              {people.slice(0, MAX_AVATARS).map((p) => (
                <span key={p.key} className="cal-agenda-avatar"><Avatar userId={Number(p.key)} name={p.label} size="xs" /></span>
              ))}
              {people.length > MAX_AVATARS && <span className="cal-agenda-more">+{people.length - MAX_AVATARS}</span>}
            </span>
          ) : <span className="cal-agenda-none">—</span>}
        </td>
      </tr>
    );
  }

  // 현재 시각 표시 — 오늘일 때, 지금보다 먼저 시작한 마지막 일정 뒤에 한 줄.
  const nowIndex = isToday ? timed.findIndex((e) => new Date(e.startAt) > now) : -1;
  const nowRow = (
    <tr className="cal-agenda-now" aria-hidden key="now">
      <td colSpan={6}><span className="cal-agenda-now-line">{formatTime(now)}</span></td>
    </tr>
  );

  const total = allDay.length + timed.length;

  return (
    <div className="cal-agenda" role="region" aria-label={tCalendar('calendar.agenda.aria')}>
      {total === 0 && !loading ? (
        <div className="cal-agenda-empty">{tCalendar('calendar.shell.day_empty')}</div>
      ) : (
        <table className="cal-agenda-table">
          {/* 열 폭 고정(2026-09-09) — 자동 레이아웃은 내용이 긴 열에 남는 폭을 비례 배분해 날짜마다 간격이 달라졌다.
              table-layout: fixed + 여기 폭으로 항상 같은 자리에. 제목만 나머지 전부. */}
          <colgroup>
            {/* 패널 폭(사이드바·레일 크기에 따라 ~800~1100px)에 비례 — 픽셀 고정이면 좁은 패널에서 제목이 너무 눌린다 */}
            <col style={{ width: '11%' }} />
            <col style={{ width: '17%' }} />
            <col />
            <col style={{ width: '10%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="cal-agenda-th-time">{tCalendar('calendar.agenda.col_time')}</th>
              <th>{tCalendar('calendar.agenda.col_calendar')}</th>
              <th>{tCalendar('calendar.agenda.col_title')}</th>
              <th>{tCalendar('calendar.agenda.col_type')}</th>
              <th>{tCalendar('calendar.agenda.col_location')}</th>
              <th>{tCalendar('calendar.agenda.col_attendees')}</th>
            </tr>
          </thead>
          <tbody>
            {allDay.map((e) => renderRow(e, (
              <td className="cal-agenda-time cal-agenda-time--allday">{tCalendar('calendar.detail.all_day')}{renderReminders(e)}</td>
            )))}
            {timed.map((e, i) => {
              // 시간 축 시안 D(2026-09-09 사용자 결정) — 큰 숫자(시) + 작은 "시작 – 종료". 12시간제.
              // 오전/오후 캡션은 각 반나절의 첫 일정에만, 앞 행과 같은 시(時)면 큰 숫자는 비워 눈금자처럼.
              const start = new Date(e.startAt);
              const prev = i > 0 ? new Date(timed[i - 1]!.startAt) : null;
              const isPm = start.getHours() >= 12;
              const firstOfHalf = !prev || (prev.getHours() >= 12) !== isPm;
              const sameHour = !!prev && prev.getHours() === start.getHours();
              const row = renderRow(e, (
                <td className={`cal-agenda-time${sameHour ? ' cal-agenda-time--cont' : ''}`}>
                  {/* "오전 8" 처럼 한 줄(2026-09-09 사용자 지시) — 캡션은 반나절 첫 행에만 */}
                  <span className="cal-agenda-time-hour" aria-hidden={sameHour || undefined}>
                    {firstOfHalf && <span className="cal-agenda-time-half">{tCalendar(isPm ? 'calendar.agenda.pm' : 'calendar.agenda.am')}</span>}
                    {hour12(start)}
                  </span>
                  {/* ⭐ 2026-10-19 — 여러 날 일정이면 날짜도(같은 달 «일» · 다른 달 «월·일»). 하루 안은 종전 그대로(lib/agendaRangeLabel.ts). */}
                  <span className="cal-agenda-time-range">{agendaRangeLabel(start, new Date(e.endAt), hm12)}</span>
                  {renderReminders(e)}
                </td>
              ));
              // 지금보다 나중에 시작하는 첫 일정 앞에 현재 시각 줄(오늘만). 전부 지났으면 맨 끝.
              return i === nowIndex ? [nowRow, row] : row;
            })}
            {isToday && nowIndex === -1 && timed.length > 0 && nowRow}
          </tbody>
        </table>
      )}
    </div>
  );
}
